import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { LoadingState, EmptyStateAllClear } from '@/components/design-system';
import { RefreshCw, ChevronRight, Clock, Sparkles, Package, Truck, Inbox, Receipt, AlertTriangle, XCircle } from 'lucide-react';

const fmtEur = (v) => `${(v || 0).toLocaleString('pt-PT', { maximumFractionDigits: 0 })}€`;

/**
 * ActionCard - Desktop-optimized card for dashboard grid
 */
const ActionCard = ({ to, value, label, supporting, icon: Icon, priority = 'default' }) => {
  const priorityStyles = {
    urgent: 'border-l-4 border-l-red-500',
    warning: 'border-l-4 border-l-amber-400',
    ai: 'border-l-4 border-l-violet-400',
    default: 'border-l-4 border-l-slate-200',
  };

  return (
    <Link
      to={to}
      className={`block bg-white border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors ${priorityStyles[priority]}`}
      data-testid={`action-card-${label?.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className="text-3xl font-mono tabular-nums font-semibold text-slate-900">
          {value?.toLocaleString()}
        </span>
        {Icon && <Icon className="h-5 w-5 text-slate-400 flex-shrink-0" />}
      </div>
      <p className="text-sm font-medium text-slate-900">{label}</p>
      {supporting && (
        <p className="text-xs text-slate-500 mt-0.5">{supporting}</p>
      )}
      <div className="flex items-center gap-1 mt-3 text-xs font-medium text-slate-500 hover:text-slate-700">
        <span>Ver detalhes</span>
        <ChevronRight className="h-3.5 w-3.5" />
      </div>
    </Link>
  );
};

/**
 * ActionRow - Mobile-optimized compact row
 */
const ActionRow = ({ to, value, label, supporting }) => (
  <Link
    to={to}
    className="flex items-center justify-between py-3 px-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 active:bg-slate-100 transition-colors"
    data-testid={`action-row-${label?.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <div className="flex items-center gap-4">
      <span className="text-2xl font-mono tabular-nums font-semibold text-slate-900 min-w-[60px]">
        {value?.toLocaleString()}
      </span>
      <div>
        <p className="text-sm font-medium text-slate-900">{label}</p>
        {supporting && <p className="text-xs text-slate-500">{supporting}</p>}
      </div>
    </div>
    <ChevronRight className="h-5 w-5 text-slate-400 flex-shrink-0" />
  </Link>
);

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/dashboard/today`);
      setData(res.data);
    } catch (error) {
      toast.error('Falha ao carregar o painel');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/sage/sync`);
      toast.success('Sincronização SAGE concluída');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Falha na sincronização');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return <LoadingState message="A carregar..." />;
  }

  const s = data?.sections || {};
  const today = new Date();
  const dateStr = today.toLocaleDateString('pt-PT', { weekday: 'long', day: 'numeric', month: 'long' });
  const totalActions = data?.total_actions || 0;

  // Build action items from sections
  const actionItems = [];
  
  if (s.urgent_buys?.count > 0) {
    actionItems.push({
      value: s.urgent_buys.count,
      label: 'Comprar hoje',
      supporting: `Risco de rutura · ${fmtEur(s.urgent_buys.total_value)}`,
      to: '/cockpit',
      icon: AlertTriangle,
      priority: 'urgent',
    });
  }
  
  if (s.stockouts?.count > 0) {
    actionItems.push({
      value: s.stockouts.count,
      label: 'Ruturas',
      supporting: `~${fmtEur(s.stockouts.total_daily_loss_eur)}/dia perdidas`,
      to: '/cockpit',
      icon: XCircle,
      priority: 'urgent',
    });
  }
  
  if (s.ai_suggestions?.count > 0) {
    actionItems.push({
      value: s.ai_suggestions.count,
      label: 'Sugestões AI',
      supporting: 'Rever sugestões',
      to: '/cockpit',
      icon: Sparkles,
      priority: 'ai',
    });
  }
  
  if (s.receptions_pending?.count > 0) {
    actionItems.push({
      value: s.receptions_pending.count,
      label: 'Encomendas por receber',
      supporting: 'A aguardar receção',
      to: '/receptions',
      icon: Package,
      priority: 'warning',
    });
  }
  
  if (s.van_replenishment?.count > 0) {
    actionItems.push({
      value: s.van_replenishment.count,
      label: 'Carrinhas a repor',
      supporting: `${s.van_replenishment.warehouses?.length || 0} armazéns`,
      to: '/van-replenishment',
      icon: Truck,
      priority: 'default',
    });
  }
  
  if (s.tars_pending?.count > 0) {
    actionItems.push({
      value: s.tars_pending.count,
      label: 'TARs por confirmar',
      supporting: 'A aguardar confirmação',
      to: '/store-transfer',
      icon: Inbox,
      priority: 'warning',
    });
  }
  
  if (s.open_aps?.count > 0) {
    actionItems.push({
      value: s.open_aps.count,
      label: 'Serviços para faturar',
      supporting: 'A aguardar faturação',
      to: '/invoicing',
      icon: Receipt,
      priority: 'default',
    });
  }

  const allClear = actionItems.length === 0;
  const hasSoon = (s.soon_buys?.count || 0) > 0;

  return (
    <div className="p-4 sm:p-6 lg:p-8" data-testid="dashboard-page">
      <div className="max-w-[1400px] mx-auto">
        
        {/* ===== PAGE HEADER - Full width ===== */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight" data-testid="dashboard-title">
              Hoje
            </h1>
            <p className="text-sm text-slate-500 capitalize mt-1">{dateStr}</p>
            
            {/* Summary KPI - Inline with header on desktop */}
            {!allClear && (
              <p className="text-slate-600 mt-2 lg:mt-3">
                <span className="text-3xl lg:text-4xl font-mono tabular-nums font-semibold text-slate-900">
                  {totalActions.toLocaleString()}
                </span>
                <span className="ml-2 text-sm">
                  {totalActions === 1 ? 'ação pendente' : 'ações pendentes'}
                </span>
              </p>
            )}
          </div>
          
          {/* Sync button - Secondary action */}
          <button
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-md transition-colors disabled:opacity-50 self-start"
            data-testid="sync-sage-button"
          >
            <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">{syncing ? 'A sincronizar...' : 'Atualizar'}</span>
          </button>
        </div>

        {/* ===== ALL CLEAR STATE ===== */}
        {allClear && (
          <div className="py-16 lg:py-24" data-testid="today-all-clear">
            <EmptyStateAllClear
              title="Tudo em dia"
              description="Sem compras urgentes, ruturas ou pendentes que precisem da tua ação hoje."
            />
          </div>
        )}

        {/* ===== ACTION CARDS - Responsive grid ===== */}
        {!allClear && (
          <>
            {/* Desktop: Grid 3 cols | Tablet: Grid 2 cols | Mobile: Hidden (use rows) */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {actionItems.map((item, idx) => (
                <ActionCard key={idx} {...item} />
              ))}
            </div>

            {/* Mobile: Compact rows */}
            <div className="md:hidden bg-white border border-slate-200 rounded-lg divide-y divide-slate-100 mb-6">
              {actionItems.map((item, idx) => (
                <ActionRow key={idx} {...item} />
              ))}
            </div>

            {/* ===== SOON BUYS - Lower priority, subtle ===== */}
            {hasSoon && (
              <div className="pt-4 border-t border-slate-100">
                <Link 
                  to="/cockpit"
                  className="flex items-center justify-between py-2 text-slate-500 hover:text-slate-700 transition-colors"
                  data-testid="action-row-soon-buys"
                >
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span className="text-sm">
                      <span className="font-medium text-slate-600">{s.soon_buys.count}</span>
                      {' '}a encomendar em breve ({fmtEur(s.soon_buys.total_value)})
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
